import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { InvalidationGroup } from '@/hooks/invalidation'
import { optimisticAdd, optimisticRemove, INVALIDATION_GROUPS } from '@/hooks/invalidation'
import { seededQuery } from '@/hooks/lists/seededQuery'
import { useDefinedQuery, type QueryDef } from '@/hooks/useDefinedQuery'
import { withIdempotency } from '@/hooks/withIdempotency'

// The collection factory lives in ./createCollectionHooks; re-exported so this stays the single factory entry point.
export { createCollectionHooks } from '@/hooks/lists/createCollectionHooks'

// List-specific config (buylist/selllist with share tokens)
interface ListHooksConfig<TList, TAddResult, TShareToken> {
  keys: {
    list: () => readonly unknown[]
    quantities: () => readonly unknown[]
    shareToken: () => readonly unknown[]
  }
  mutationKeys: {
    add: readonly unknown[]
    remove: readonly unknown[]
  }
  api: {
    getList: () => Promise<TList>
    addItem: (cardId: number, quantity?: number, idempotencyKey?: string) => Promise<TAddResult>
    removeItem: (cardId: number, idempotencyKey?: string, all?: boolean) => Promise<void>
    getShareToken: () => Promise<TShareToken>
    regenerateShareToken: (idempotencyKey?: string) => Promise<TShareToken>
  }
  group: InvalidationGroup
}

// Factory for list hooks (buylist/selllist)
export function createListHooks<TList, TAddResult, TShareToken>(
  config: ListHooksConfig<TList, TAddResult, TShareToken>,
) {
  // `auth: 'required'` gates `enabled` on `isAuthenticated` — identical to the
  // previous `useAuthQuery` behavior, now via the documented `useDefinedQuery`
  // primitive (see hooks/CLAUDE.md).
  const listQuery: QueryDef<TList> = {
    queryKey: config.keys.list,
    queryFn: config.api.getList,
    auth: 'required',
  }
  function useList() {
    return useDefinedQuery(listQuery)
  }

  // Data seeded by useUserQuantities() — never actually fetches (see seededQuery).
  const quantitiesQuery = seededQuery(config.keys.quantities)
  function useQuantities() {
    return useDefinedQuery(quantitiesQuery)
  }

  function useAdd() {
    const queryClient = useQueryClient()
    type AddVars = { cardId: number; quantity?: number; idempotencyKey?: string }
    const mutation = useMutation({
      mutationKey: config.mutationKeys.add,
      mutationFn: ({ cardId, quantity, idempotencyKey }: AddVars) =>
        config.api.addItem(cardId, quantity, idempotencyKey),
      ...optimisticAdd<AddVars>(
        queryClient,
        config.keys.quantities(),
        INVALIDATION_GROUPS[config.group],
        (v) => v.cardId,
        (v) => v.quantity ?? 1,
      ),
    })
    return withIdempotency(mutation)
  }

  function useRemove() {
    const queryClient = useQueryClient()
    type RemoveVars = { cardId: number; all?: boolean; idempotencyKey?: string }
    const mutation = useMutation({
      mutationKey: config.mutationKeys.remove,
      mutationFn: (vars: RemoveVars) => config.api.removeItem(vars.cardId, vars.idempotencyKey, vars.all || undefined),
      ...optimisticRemove<RemoveVars>(
        queryClient,
        config.keys.quantities(),
        INVALIDATION_GROUPS[config.group],
        (v) => v.cardId,
        { getAll: (v) => v.all ?? false },
      ),
    })
    type PublicVars = number | RemoveVars
    const normalize = (vars: PublicVars): RemoveVars => (typeof vars === 'number' ? { cardId: vars } : vars)
    return withIdempotency(mutation, normalize)
  }

  const shareTokenQuery: QueryDef<TShareToken> = {
    queryKey: config.keys.shareToken,
    queryFn: config.api.getShareToken,
    auth: 'required',
  }
  function useShareToken() {
    return useDefinedQuery(shareTokenQuery)
  }

  function useRegenerateToken() {
    const queryClient = useQueryClient()
    type RegenVars = { idempotencyKey?: string }
    const mutation = useMutation({
      mutationFn: ({ idempotencyKey }: RegenVars = {}) => config.api.regenerateShareToken(idempotencyKey),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: config.keys.shareToken() })
      },
    })
    return withIdempotency(mutation, (vars?: RegenVars) => vars ?? {})
  }

  return {
    useList,
    useQuantities,
    useAdd,
    useRemove,
    useShareToken,
    useRegenerateToken,
  }
}
